import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import ProjectVitalIssueDialog from '../components/ProjectVitalIssueDialog';
import ProjectVitalTaskDialog from '../components/ProjectVitalTaskDialog';
import {
  listProjectVitalIssues,
  listProjectVitalTasks,
  updateProjectVitalTask,
} from '../services/projectVitalService';
import type {
  ProjectVitalIssue,
  ProjectVitalIssueStatus,
  ProjectVitalTask,
} from '../types/projectVital';

type StatusFilter = 'todos' | ProjectVitalIssueStatus;

const STATUS_META: Record<
  ProjectVitalIssueStatus,
  {
    label: string;
    color: string;
    background: string;
  }
> = {
  pendiente: {
    label: 'Pendiente',
    color: '#cbd5e1',
    background: 'rgba(148,163,184,0.10)',
  },
  en_curso: {
    label: 'En curso',
    color: '#67e8f9',
    background: 'rgba(34,211,238,0.10)',
  },
  esperando: {
    label: 'Esperando',
    color: '#fde68a',
    background: 'rgba(250,204,21,0.10)',
  },
  bloqueado: {
    label: 'Bloqueado',
    color: '#fca5a5',
    background: 'rgba(248,113,113,0.10)',
  },
  resuelto: {
    label: 'Resuelto',
    color: '#86efac',
    background: 'rgba(74,222,128,0.10)',
  },
};

function formatDate(value: string | null) {
  if (!value) {
    return null;
  }

  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${value}T00:00:00`));
}

function isOverdue(value: string | null) {
  if (!value) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const target = new Date(`${value}T00:00:00`);

  return target < today;
}

export default function ProjectVitalIssuesView() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] =
    useState<StatusFilter>('todos');

  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [editingIssue, setEditingIssue] =
    useState<ProjectVitalIssue | null>(null);

  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskIssue, setTaskIssue] =
    useState<ProjectVitalIssue | null>(null);

  const issuesQuery = useQuery({
    queryKey: ['project-vital', 'issues'],
    queryFn: listProjectVitalIssues,
  });

  const tasksQuery = useQuery({
    queryKey: ['project-vital', 'tasks'],
    queryFn: listProjectVitalTasks,
  });

  const completeTaskMutation = useMutation({
    mutationFn: async (task: ProjectVitalTask) => {
      return updateProjectVitalTask(task.id, {
        title: task.title,
        area: task.area,
        status: 'completada',
        due_date: task.due_date,
        is_important: task.is_important,
        issue_id: task.issue_id,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['project-vital', 'tasks'],
      });

      enqueueSnackbar('Tarea completada.', {
        variant: 'success',
      });
    },
    onError: () => {
      enqueueSnackbar('No se pudo completar la tarea.', {
        variant: 'error',
      });
    },
  });

  const tasksByIssue = useMemo(() => {
    const map = new Map<string, ProjectVitalTask[]>();

    for (const task of tasksQuery.data ?? []) {
      if (!task.issue_id) {
        continue;
      }

      const current = map.get(task.issue_id) ?? [];
      current.push(task);
      map.set(task.issue_id, current);
    }

    return map;
  }, [tasksQuery.data]);

  const filteredIssues = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return (issuesQuery.data ?? []).filter((issue) => {
      if (
        statusFilter !== 'todos' &&
        issue.status !== statusFilter
      ) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      return [
        issue.title,
        issue.area,
        issue.next_step,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch);
    });
  }, [
    issuesQuery.data,
    search,
    statusFilter,
  ]);

  const summary = useMemo(() => {
    const issues = issuesQuery.data ?? [];

    return {
      activos: issues.filter(
        (issue) =>
          issue.status === 'pendiente' ||
          issue.status === 'en_curso',
      ).length,
      esperando: issues.filter(
        (issue) => issue.status === 'esperando',
      ).length,
      bloqueados: issues.filter(
        (issue) => issue.status === 'bloqueado',
      ).length,
      resueltos: issues.filter(
        (issue) => issue.status === 'resuelto',
      ).length,
    };
  }, [issuesQuery.data]);

  if (issuesQuery.isLoading || tasksQuery.isLoading) {
    return (
      <Box
        sx={{
          minHeight: 420,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'flex-end' }}
          spacing={2}
        >
          <Box>
            <Typography
              component="h1"
              sx={{
                color: '#f8fafc',
                fontWeight: 900,
                fontSize: { xs: '1.9rem', md: '2.35rem' },
                letterSpacing: '-0.9px',
                lineHeight: 1.05,
              }}
            >
              Asuntos
            </Typography>

            <Typography
              sx={{
                mt: 0.8,
                color: '#94a3b8',
                fontSize: { xs: '0.92rem', md: '0.98rem' },
              }}
            >
              Situaciones abiertas, próximo paso y tareas relacionadas.
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={() => {
              setEditingIssue(null);
              setIssueDialogOpen(true);
            }}
            sx={{
              fontWeight: 800,
              bgcolor: '#4DD0E1',
              color: '#07111f',
              '&:hover': {
                bgcolor: '#88FFFF',
              },
            }}
          >
            Nuevo asunto
          </Button>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 1.25,
          }}
        >
          {[
            ['Activos', summary.activos],
            ['Esperando', summary.esperando],
            ['Bloqueados', summary.bloqueados],
            ['Resueltos', summary.resueltos],
          ].map(([label, value]) => (
            <Paper
              key={label}
              elevation={0}
              sx={{
                p: 1.8,
                borderRadius: 3,
                bgcolor: '#111c2f',
                border: `1px solid ${alpha('#94a3b8', 0.12)}`,
              }}
            >
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.74rem',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                }}
              >
                {label}
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  color: '#f8fafc',
                  fontWeight: 900,
                  fontSize: '1.65rem',
                }}
              >
                {value}
              </Typography>
            </Paper>
          ))}
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: 3.25,
            bgcolor: alpha('#ffffff', 0.018),
            border: `1px solid ${alpha('#94a3b8', 0.10)}`,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
          >
            <TextField
              size="small"
              placeholder="Buscar asuntos"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchRoundedIcon
                    sx={{
                      mr: 1,
                      color: '#64748b',
                      fontSize: 19,
                    }}
                  />
                ),
              }}
              sx={{
                flex: 1,
              }}
            />

            <FormControl
              size="small"
              sx={{
                minWidth: { sm: 170 },
              }}
            >
              <InputLabel>Estado</InputLabel>

              <Select
                label="Estado"
                value={statusFilter}
                onChange={(event) =>
                  setStatusFilter(
                    event.target.value as StatusFilter,
                  )
                }
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="pendiente">Pendiente</MenuItem>
                <MenuItem value="en_curso">En curso</MenuItem>
                <MenuItem value="esperando">Esperando</MenuItem>
                <MenuItem value="bloqueado">Bloqueado</MenuItem>
                <MenuItem value="resuelto">Resuelto</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        {filteredIssues.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 3.5 },
              borderRadius: 3.5,
              bgcolor: '#111c2f',
              border: `1px solid ${alpha('#94a3b8', 0.12)}`,
            }}
          >
            <Typography
              sx={{
                color: '#e2e8f0',
                fontWeight: 800,
              }}
            >
              No hay asuntos que mostrar.
            </Typography>

            <Typography
              sx={{
                mt: 0.6,
                color: '#64748b',
                fontSize: '0.86rem',
              }}
            >
              Crea un asunto para empezar a agrupar seguimiento, tareas y próximos pasos.
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                xl: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 1.5,
            }}
          >
            {filteredIssues.map((issue) => {
              const issueTasks =
                tasksByIssue.get(issue.id) ?? [];

              const pendingTasks = issueTasks.filter(
                (task) =>
                  task.status === 'pendiente' ||
                  task.status === 'en_curso',
              );

              const completedTasks = issueTasks.filter(
                (task) => task.status === 'completada',
              );

              const statusMeta = STATUS_META[issue.status];
              const overdue =
                issue.status !== 'resuelto' &&
                isOverdue(issue.target_date);

              return (
                <Paper
                  key={issue.id}
                  elevation={0}
                  sx={{
                    p: { xs: 2, md: 2.4 },
                    borderRadius: 3.5,
                    bgcolor: '#111c2f',
                    border: `1px solid ${
                      overdue
                        ? alpha('#f87171', 0.26)
                        : alpha('#94a3b8', 0.12)
                    }`,
                  }}
                >
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Stack
                          direction="row"
                          spacing={0.8}
                          alignItems="center"
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <Chip
                            label={statusMeta.label}
                            size="small"
                            sx={{
                              color: statusMeta.color,
                              bgcolor: statusMeta.background,
                              border: `1px solid ${alpha(
                                statusMeta.color,
                                0.20,
                              )}`,
                            }}
                          />

                          <Chip
                            label={issue.area}
                            size="small"
                            variant="outlined"
                            sx={{
                              color: '#94a3b8',
                              borderColor: alpha('#94a3b8', 0.14),
                            }}
                          />

                          {overdue ? (
                            <Chip
                              label="Fecha superada"
                              size="small"
                              sx={{
                                color: '#fca5a5',
                                bgcolor: alpha('#f87171', 0.08),
                              }}
                            />
                          ) : null}
                        </Stack>

                        <Typography
                          sx={{
                            mt: 1.25,
                            color: '#f8fafc',
                            fontWeight: 850,
                            fontSize: '1.08rem',
                            lineHeight: 1.35,
                          }}
                        >
                          {issue.title}
                        </Typography>
                      </Box>

                      <Tooltip title="Editar asunto">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingIssue(issue);
                            setIssueDialogOpen(true);
                          }}
                          sx={{
                            color: '#64748b',
                          }}
                        >
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        bgcolor: alpha('#ffffff', 0.022),
                        border: `1px solid ${alpha('#94a3b8', 0.08)}`,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                      >
                        <FlagRoundedIcon
                          sx={{
                            mt: 0.15,
                            color: '#4DD0E1',
                            fontSize: 18,
                          }}
                        />

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              color: '#64748b',
                              fontSize: '0.7rem',
                              fontWeight: 800,
                              textTransform: 'uppercase',
                              letterSpacing: 0.7,
                            }}
                          >
                            Próximo paso
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.35,
                              color: issue.next_step
                                ? '#cbd5e1'
                                : '#64748b',
                              fontSize: '0.84rem',
                              lineHeight: 1.45,
                            }}
                          >
                            {issue.next_step ||
                              'Sin próximo paso definido'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={2}
                    >
                      <Typography
                        sx={{
                          color: '#64748b',
                          fontSize: '0.76rem',
                        }}
                      >
                        {issue.target_date
                          ? `Objetivo: ${formatDate(issue.target_date)}`
                          : 'Sin fecha objetivo'}
                      </Typography>

                      <Typography
                        sx={{
                          color: '#64748b',
                          fontSize: '0.76rem',
                        }}
                      >
                        {pendingTasks.length} pendientes ·{' '}
                        {completedTasks.length} completadas
                      </Typography>
                    </Stack>

                    {pendingTasks.length > 0 ? (
                      <Stack spacing={0.7}>
                        {pendingTasks.slice(0, 4).map((task) => (
                          <Box
                            key={task.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 1,
                              borderRadius: 2,
                              bgcolor: alpha('#ffffff', 0.018),
                            }}
                          >
                            <Tooltip title="Marcar como completada">
                              <IconButton
                                size="small"
                                disabled={completeTaskMutation.isPending}
                                onClick={() =>
                                  completeTaskMutation.mutate(task)
                                }
                                sx={{
                                  color: '#64748b',
                                  '&:hover': {
                                    color: '#86efac',
                                  },
                                }}
                              >
                                <CheckCircleOutlineRoundedIcon
                                  fontSize="small"
                                />
                              </IconButton>
                            </Tooltip>

                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                noWrap
                                sx={{
                                  color: '#cbd5e1',
                                  fontSize: '0.82rem',
                                  fontWeight: 700,
                                }}
                              >
                                {task.title}
                              </Typography>

                              {task.due_date ? (
                                <Typography
                                  sx={{
                                    mt: 0.15,
                                    color: '#64748b',
                                    fontSize: '0.7rem',
                                  }}
                                >
                                  {formatDate(task.due_date)}
                                </Typography>
                              ) : null}
                            </Box>

                            {task.is_important ? (
                              <Chip
                                label="Importante"
                                size="small"
                                variant="outlined"
                                sx={{
                                  color: '#fde68a',
                                  borderColor: alpha('#fde68a', 0.18),
                                }}
                              />
                            ) : null}
                          </Box>
                        ))}

                        {pendingTasks.length > 4 ? (
                          <Typography
                            sx={{
                              color: '#64748b',
                              fontSize: '0.74rem',
                              pl: 1,
                            }}
                          >
                            + {pendingTasks.length - 4} tareas más
                          </Typography>
                        ) : null}
                      </Stack>
                    ) : (
                      <Typography
                        sx={{
                          color: '#64748b',
                          fontSize: '0.8rem',
                        }}
                      >
                        Sin tareas pendientes vinculadas.
                      </Typography>
                    )}

                    {issue.status !== 'resuelto' ? (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<TaskAltRoundedIcon />}
                        onClick={() => {
                          setTaskIssue(issue);
                          setTaskDialogOpen(true);
                        }}
                        sx={{
                          alignSelf: 'flex-start',
                          color: '#cbd5e1',
                          borderColor: alpha('#94a3b8', 0.18),
                        }}
                      >
                        Añadir tarea
                      </Button>
                    ) : null}
                  </Stack>
                </Paper>
              );
            })}
          </Box>
        )}
      </Stack>

      <ProjectVitalIssueDialog
        open={issueDialogOpen}
        issue={editingIssue}
        onClose={() => {
          setIssueDialogOpen(false);
          setEditingIssue(null);
        }}
      />

      <ProjectVitalTaskDialog
        open={taskDialogOpen}
        defaultIssueId={taskIssue?.id ?? null}
        defaultArea={taskIssue?.area ?? null}
        onClose={() => {
          setTaskDialogOpen(false);
          setTaskIssue(null);
        }}
      />
    </>
  );
}
